import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useLocation } from 'react-router-dom';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { MERCHANT_REQUESTS } from 'gql/queries';
import { RequestStatusBadge } from 'components/RequestStatusBadge';
import {
  canManageRequests,
  MerchantRequestsQueryResult,
  MerchantRequestType,
} from 'types/resolution';
import { isShipaidAdmin } from 'utils';
import { getMerchantRequestsContext } from 'utils/merchantRequestsTransport';
import { requestHasRelevantSettlement } from 'utils/requestSettlementPresentation';
import { getRequestTypeLabel } from 'utils/requestTypePresentation';
import {
  getRequestReturnTo,
  withRequestReturnTo,
} from 'utils/requestNavigation';

interface OrderRequestsPanelProps {
  orderId: string;
  currentRequest?: { type: MerchantRequestType; id: string };
  refreshKey?: string | null;
}

const detailLabel = (value: string) =>
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const RequestHistory = ({
  orderId,
  currentRequest,
  storeId,
}: OrderRequestsPanelProps & { storeId: string }) => {
  const [offset, setOffset] = useState(0);
  const location = useLocation();
  const sourceList = `/${location.pathname.split('/')[1]}`;
  const returnTo = getRequestReturnTo(
    location.search,
    (location.state as { returnLocation?: string } | null)?.returnLocation,
    ['/orders', '/claims', '/returns'].includes(sourceList)
      ? sourceList
      : '/requests',
  );
  const { data, loading, error, refetch } =
    useQuery<MerchantRequestsQueryResult>(MERCHANT_REQUESTS, {
      context: getMerchantRequestsContext(),
      variables: {
        input: {
          storeId,
          orderId,
          group: 'ALL',
          type: 'ALL',
          limit: 20,
          offset,
        },
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });
  const history = data?.merchantRequests;

  if (loading)
    return (
      <p role="status" className="p-4 text-sm text-gray-500">
        Loading requests…
      </p>
    );
  if (error || !history || history.storeId !== storeId) {
    return (
      <div role="alert" className="p-4 text-sm text-red-700">
        Unable to load requests for this order.
        <button
          type="button"
          className="ml-2 font-medium underline"
          onClick={() => {
            void refetch();
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  if (history.totalCount === 0)
    return (
      <p className="p-4 text-sm text-gray-500">No requests for this order.</p>
    );

  return (
    <>
      <div className="px-4 py-2 text-sm text-gray-600">
        {history.totalCount} {history.totalCount === 1 ? 'request' : 'requests'}{' '}
        on this order
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
            <tr>
              <th scope="col" className="px-4 py-2">
                Request
              </th>
              <th scope="col" className="px-4 py-2">
                Status
              </th>
              <th scope="col" className="px-4 py-2">
                Next step
              </th>
              <th scope="col" className="px-4 py-2">
                Filed
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {history.nodes.map((request) => {
              const typeLabel = getRequestTypeLabel(request);
              const path = `/${
                request.type === 'ISSUE' ? 'claims' : 'returns'
              }/${request.id}`;
              const current =
                currentRequest?.id === request.id &&
                currentRequest.type === request.type;
              const filedDate = request.createdAt
                ? new Date(request.createdAt)
                : null;
              return (
                <tr
                  key={`${request.type}:${request.id}`}
                  className={current ? 'bg-blue-50' : undefined}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link
                      to={withRequestReturnTo(path, returnTo)}
                      className="font-medium text-blue-600 hover:underline"
                      aria-current={current ? 'page' : undefined}
                    >
                      {typeLabel} {request.number}
                    </Link>
                    {current && (
                      <span className="ml-2 text-xs text-gray-500">
                        Current
                      </span>
                    )}
                    {request.items?.length > 0 && (
                      <div className="mt-1 space-y-1 text-xs text-gray-500 whitespace-normal">
                        {request.items.slice(0, 2).map((item, index) => (
                          <p key={item.orderItemId || index}>
                            {item.quantity == null ? '' : `${item.quantity} × `}
                            {item.title || 'Item'}
                          </p>
                        ))}
                        {request.items.length > 2 && (
                          <p>
                            +{request.items.length - 2} more{' '}
                            {request.items.length === 3 ? 'item' : 'items'}
                          </p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <RequestStatusBadge
                      status={request.status}
                      label={request.statusLabel}
                    />
                    {request.outcome && (
                      <p className="mt-1 text-xs text-gray-500">
                        Outcome: {detailLabel(request.outcome)}
                      </p>
                    )}
                    {requestHasRelevantSettlement(request) && (
                      <p className="mt-1 text-xs text-gray-500">
                        Settlement:{' '}
                        {[request.settlementType, request.settlementStatus]
                          .filter((value): value is string => Boolean(value))
                          .map(detailLabel)
                          .join(' · ')}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {request.nextActionLabel || '—'}
                    {request.responsibleParty && (
                      <p className="mt-1 text-xs text-gray-500">
                        Waiting on: {detailLabel(request.responsibleParty)}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                    {filedDate && !Number.isNaN(filedDate.getTime())
                      ? filedDate.toLocaleDateString()
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(history.hasMore || offset > 0) && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm">
          <span className="text-gray-500">
            {offset + 1}–{offset + history.nodes.length} of {history.totalCount}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              aria-label="Previous requests"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
              className="rounded border px-3 py-1 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              aria-label="Next requests"
              disabled={!history.hasMore}
              onClick={() => setOffset(offset + 20)}
              className="rounded border px-3 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export const OrderRequestsPanel = (props: OrderRequestsPanelProps) => {
  const { storeId } = useStore();
  const { role, user } = useUser();
  const allowed = canManageRequests(role, isShipaidAdmin(user));

  return (
    <section
      aria-label="Related requests"
      className="mb-6 rounded-lg border border-gray-200"
    >
      <div className="border-b border-gray-200 px-4 py-3">
        <h2 className="font-semibold text-gray-900">Requests</h2>
        <p className="mt-1 text-xs text-gray-500">
          Each request keeps its own status and resolution.
        </p>
      </div>
      {!allowed ? (
        <p className="p-4 text-sm text-gray-500">
          Admin or manager access is required to view request history.
        </p>
      ) : !storeId || !props.orderId ? (
        <p className="p-4 text-sm text-gray-500">
          Order context is unavailable.
        </p>
      ) : (
        <RequestHistory
          key={`${storeId}:${props.orderId}:${props.refreshKey || ''}`}
          {...props}
          storeId={storeId}
        />
      )}
    </section>
  );
};
